import React from "react";
import { Badge, UncontrolledTooltip } from "reactstrap";

const fmtDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d) ? value : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtAmount = (value, currency) => {
  const n = Number(value || 0);
  return `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency || ""}`.trim();
};

const STATUS_LABELS = { saved: "Saved", paid: "Paid", overdue: "Overdue" };

const fmtChangeValue =(value) => (value === "" || value == null ? "—" : value);

const StatusIcon = ({ invoice, index }) => {
  const id = `imp-status-${index}`;
  if (invoice.ready_to_save && invoice.import_action === "update") {
    return (
      <>
        <i id={id} className="ri-refresh-line text-info fs-5" style={{ cursor: "help" }} />
        <UncontrolledTooltip target={id}>
          <div className="fw-bold mb-1">Modified - updates the existing invoice</div>
          <ul className="mb-0 ps-3 text-start">
            {(invoice.changes || []).map((c) => (
              <li key={c.field}>{c.label}: {fmtChangeValue(c.old)} → {fmtChangeValue(c.new)}</li>
            ))}
          </ul>
        </UncontrolledTooltip>
      </>
    );
  }
  if (invoice.ready_to_save) {
    return (
      <>
        <i id={id} className="ri-checkbox-circle-fill text-success fs-5" />
        <UncontrolledTooltip target={id}>Ready to import</UncontrolledTooltip>
      </>
    );
  }
  return (
    <>
      <i id={id} className="ri-error-warning-fill text-warning fs-5" style={{ cursor: "help" }} />
      <UncontrolledTooltip target={id}>
        <div className="fw-bold mb-1">{invoice.issues?.length || 0} issue{(invoice.issues?.length || 0) !== 1 ? "s" : ""}</div>
        <ul className="mb-0 ps-3 text-start">
          {(invoice.issues || []).map((issue, i) => <li key={i}>{issue}</li>)}
        </ul>
      </UncontrolledTooltip>
    </>
  );
};

const ImportRow = ({ invoice, index, onEdit, onDelete, onToggle }) => {
  const isUpdate = invoice.import_action === "update";
  const changed = new Set((invoice.changes || []).map((c) => c.field));
  // Highlight cells whose value differs from the invoice already in the DB
  const cell = (fields, base) => `${base}${fields.some((f) => changed.has(f)) ? " bg-info-subtle" : ""}`;

  return (
  <tr className={invoice.has_issues ? "table-warning" : ""}>
    <td className="text-center px-2 py-2">
      <input type="checkbox" className="form-check-input" checked={!!invoice.selected}
        disabled={!invoice.ready_to_save} onChange={() => onToggle(index)}
        title={invoice.ready_to_save ? "Include in import" : "Resolve issues before importing"} />
    </td>
    <td className="text-center px-3 py-2"><StatusIcon invoice={invoice} index={index} /></td>
    <td className="px-2 py-2">
      <span className="fw-medium text-primary small">{invoice.document || <span className="text-danger fst-italic">missing</span>}</span>
      {isUpdate && <Badge color="info" className="ms-1 fw-normal">Modified</Badge>}
    </td>
    <td className={cell(["cif"], "px-2 py-2")}><span className="font-monospace small">{invoice.cif || <span className="text-danger fst-italic">missing</span>}</span></td>
    <td className={cell(["name"], "px-2 py-2 small text-muted")}>{invoice.name || <span className="text-danger fst-italic">missing</span>}</td>
    <td className={cell(["issue_date"], "px-2 py-2 small text-muted")}>{fmtDate(invoice.issue_date)}</td>
    <td className={cell(["due_date"], "px-2 py-2 small text-muted")}>{fmtDate(invoice.due_date)}</td>
    <td className={cell(["total_amount", "amount_without_tax", "tax_amount", "currency"], "px-2 py-2 small fw-semibold text-nowrap")}>{fmtAmount(invoice.total_amount, invoice.currency)}</td>
    <td className={cell(["status"], "px-2 py-2 small text-nowrap")}>
      {STATUS_LABELS[invoice.status] || invoice.status || "—"}
      {changed.has("status") && (
        <div className="text-muted" style={{ fontSize: 11 }}>
          was {STATUS_LABELS[(invoice.changes.find((c) => c.field === "status") || {}).old] || "—"}
        </div>
      )}
    </td>
    <td className={cell(["phase"], "px-2 py-2 small")}>{invoice.phase?.phase_name || <span className="text-danger fst-italic">—</span>}</td>
    <td className={cell(["category"], "px-2 py-2 small")}>{invoice.category?.category_name || <span className="text-danger fst-italic">—</span>}</td>
    <td className={cell(["sub_category"], "px-2 py-2 small text-muted")}>{invoice.sub_category?.sub_category_name || "—"}</td>
    <td className={cell(["block"], "px-2 py-2 small text-muted")}>{invoice.block?.block_name || "—"}</td>
    <td className="text-end px-3 py-2 text-nowrap">
      <button className="btn btn-sm btn-icon btn-ghost-secondary" onClick={() => onEdit(index)} title="Edit invoice">
        <i className="ri-pencil-line fs-16" />
      </button>
      <button className="btn btn-sm btn-icon btn-ghost-danger" onClick={() => onDelete(index)} title="Delete invoice">
        <i className="ri-delete-bin-5-line fs-16" />
      </button>
    </td>
  </tr>
  );
};

export default ImportRow;
